import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Play,
  Star,
  CheckCircle,
  ArrowRight,
  BookOpen,
  Users,
  Globe,
  GraduationCap,
  Video,
  Award,
  ChevronRight,
  Quote,
  Calendar,
  MapPin,
  Clock,
  Sparkles
} from 'lucide-react';
import { mockTestimonials } from '../data/mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteLinks, setSiteLinks] = useState({
    webinar: { url: 'https://fengshuisignature.com/optin', text: 'Join the upcoming webinar', enabled: true },
    signatureOfWealth: { url: 'https://fengshuisignature.com/optin', text: 'Join the upcoming webinar', enabled: true },
    heroConsultation: { url: '/consultation-fees', text: 'Book Consultation', enabled: true },
    heroCourses: { url: '/courses', text: 'Explore Courses', enabled: true }
  });
  const [featuredEvent, setFeaturedEvent] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [sectionSettings, setSectionSettings] = useState({});

  const getSec = (key, field, fallback) => sectionSettings[key]?.[field] || fallback;

  const renderCta = (key, fallbackText, fallbackLink, fallbackExternal) => {
    const text = getSec(key, 'cta_text', fallbackText);
    const link = getSec(key, 'cta_link', fallbackLink);
    const external = getSec(key, 'cta_external', fallbackExternal);
    if (external) {
      return (
        <a href={link} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center bg-amber-500 hover:bg-amber-400 text-[#0a0a0a] font-medium px-6 py-3 rounded-md transition-colors">
          {text}<ArrowRight className="w-4 h-4 ml-2" />
        </a>
      );
    }
    return (
      <div onClick={() => navigate(link)}
        className="inline-flex items-center bg-amber-500 hover:bg-amber-400 text-[#0a0a0a] font-medium px-6 py-3 rounded-md transition-colors cursor-pointer">
        {text}<ArrowRight className="w-4 h-4 ml-2" />
      </div>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch featured courses
        const coursesResponse = await fetch(`${BACKEND_URL}/api/courses/featured?limit=3`);
        if (coursesResponse.ok) {
          const data = await coursesResponse.json();
          setFeaturedCourses(data);
        }
        
        // Fetch site links
        const linksResponse = await fetch(`${BACKEND_URL}/api/site-links`);
        if (linksResponse.ok) {
          const linksData = await linksResponse.json();
          setSiteLinks(prev => ({ ...prev, ...linksData }));
        }
        
        // Fetch events
        const eventsResponse = await fetch(`${BACKEND_URL}/api/events`);
        if (eventsResponse.ok) {
          const eventsData = await eventsResponse.json();
          setFeaturedEvent(eventsData.featured || null);
          setUpcomingEvents(eventsData.upcoming || []);
        }

        // Fetch section settings
        const sectionsResponse = await fetch(`${BACKEND_URL}/api/sections`);
        if (sectionsResponse.ok) {
          const sectionsData = await sectionsResponse.json();
          setSectionSettings(sectionsData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const academies = [
    {
      title: 'BaZi Academy',
      subtitle: 'Destiny Analysis',
      description: 'Understand your strengths, limitations, and hidden talents. Discover your core values and life blueprint through the Four Pillars of Destiny.',
      image: 'https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?w=600&h=400&fit=crop',
      color: 'amber'
    },
    {
      title: 'Feng Shui Academy',
      subtitle: 'Environmental Energy',
      description: 'Master the energy flow of your environments for personal wellbeing and professional success. Transform your space, transform your life.',
      image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&h=400&fit=crop',
      color: 'emerald'
    },
    {
      title: 'QiMen Academy',
      subtitle: 'Strategic Forecasting',
      description: 'Gain situational awareness to craft winning strategies for all areas of your life. Master the art of timing and direction.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=400&fit=crop',
      color: 'cyan'
    },
    {
      title: 'Date Selection',
      subtitle: 'Online TongShu',
      description: 'Choose auspicious dates for important events using the traditional Chinese almanac. Optimize timing for weddings, business launches, and more.',
      image: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=600&h=400&fit=crop',
      color: 'rose'
    },
    {
      title: 'Numerology Academy',
      subtitle: 'Number Wisdom',
      description: 'Decode the hidden meanings in numbers that influence your personality, relationships, and life path. Unlock numerical insights.',
      image: 'https://images.unsplash.com/photo-1509228468518-180dd4864904?w=600&h=400&fit=crop',
      color: 'purple'
    }
  ];

  const consultationTypes = [
    {
      category: 'Destiny Reading',
      title: 'BaZi Analysis',
      description: 'Are you truly living up to your fullest potential right now? Discover your personal life blueprint.',
      icon: GraduationCap
    },
    {
      category: 'Strategic Timing',
      title: 'QiMen Forecasting',
      description: 'Want to know when is truly the right time to make that significant leap of faith?',
      icon: Sparkles
    },
    {
      category: 'Space Harmony',
      title: 'Feng Shui Audit',
      description: 'Did you know the arrangement of your space is silently impacting your mood and luck?',
      icon: Award
    },
    {
      category: 'Life Path',
      title: 'Numerology Reading',
      description: 'Curious about what your birth numbers reveal about your destiny and life purpose?',
      icon: Video
    }
  ];

  const stats = [
    { value: '10,000+', label: 'Influenced Students Worldwide' },
    { value: '15+', label: 'Years Experience' },
    { value: '1000+', label: 'Training Video Hours' },
    { value: '20+', label: 'Countries Reached' }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Hero Section - Full Screen with Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background with Overlay */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[#0a0a0a]" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left: Text */}
            <div>
            <p className="text-amber-400 text-sm md:text-base font-medium tracking-widest uppercase mb-6">
              Align your energy. Create your luck. Start winning with strategy.
            </p>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-white mb-6 leading-[1.1]">
              Master the <span className="font-serif italic text-amber-200">system.</span>
              <br />
              Master the <span className="font-serif italic text-amber-200">outcome.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-xl leading-relaxed">
              Align your timing, environment, and choices through a proven Chinese Metaphysics system — practical, precise, and results-driven.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={() => navigate('/consultation-fees')}
                className="bg-transparent border-2 border-white/80 text-white hover:bg-white hover:text-[#0a0a0a] px-8 py-6 text-base font-medium tracking-wide transition-all duration-300"
                data-testid="hero-explore-btn"
              >
                Book Consultation
              </Button>
              <Button
                size="lg"
                variant="ghost"
                onClick={() => navigate('/courses')}
                className="text-white/80 hover:text-white hover:bg-white/10 px-8 py-6 text-base font-medium tracking-wide"
                data-testid="hero-about-btn"
              >
                Explore Courses
              </Button>
            </div>
            
            {/* Webinar CTA */}
            {siteLinks.webinar?.enabled && (
              <div className="mt-8">
                <a 
                  href={siteLinks.webinar?.url || 'https://fengshuisignature.com/optin'} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-400 text-black px-8 py-4 rounded-lg font-medium text-lg transition-all duration-300 group"
                >
                  {siteLinks.webinar?.text || 'Join the upcoming webinar'}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            )}
            </div>

            {/* Right: Celestial Wheel Animation */}
            <div className="flex items-center justify-center relative lg:order-2 order-first" data-testid="celestial-wheel">
              <style>{`
                @keyframes cantian-orbit-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
              `}</style>

              <div className="relative w-[calc(100vw-32px)] max-w-[420px] sm:max-w-[460px] lg:max-w-[560px]" style={{ aspectRatio: '1' }}>
                {/* Background disc */}
                <div className="absolute rounded-full" style={{ inset: '8%', background: 'rgba(5,5,5,0.4)', border: '1px solid rgba(212,175,55,0.12)' }} />

                {/* Star chart image - slow spin */}
                <div className="pointer-events-none absolute overflow-hidden rounded-full" style={{ inset: '8%', opacity: 0.7 }}>
                  <img
                    src="https://www.cantian.ai/images/home/starchart.png"
                    alt=""
                    aria-hidden="true"
                    className="h-full w-full object-cover"
                    style={{ animation: 'cantian-orbit-spin 160s linear infinite', filter: 'sepia(0.6) hue-rotate(-10deg) brightness(0.7)' }}
                  />
                </div>

                {/* Orbit ring 1 - outermost */}
                <div className="pointer-events-none absolute box-border origin-center rounded-full" style={{ inset: '13%', border: '1px solid rgba(212,175,55,0.18)' }}>
                  <div className="relative h-full w-full origin-center" style={{ animation: 'cantian-orbit-spin 28s linear infinite' }}>
                    <div className="absolute inset-0 origin-center" style={{ transform: 'rotate(16deg)' }}>
                      <span className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ width: 11, height: 11, backgroundColor: '#f3c776', border: '1.5px solid rgba(255,248,236,0.9)', boxShadow: '0 0 34px #f6deb0, 0 0 0 3px rgba(255,239,210,0.45)' }} />
                    </div>
                  </div>
                </div>

                {/* Orbit ring 2 */}
                <div className="pointer-events-none absolute box-border origin-center rounded-full" style={{ inset: '20%', border: '1px solid rgba(212,175,55,0.14)' }}>
                  <div className="relative h-full w-full origin-center" style={{ animation: 'cantian-orbit-spin 14s linear infinite' }}>
                    <div className="absolute inset-0 origin-center" style={{ transform: 'rotate(98deg)' }}>
                      <span className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ width: 9, height: 9, backgroundColor: '#f0ddba', border: '1.5px solid rgba(255,248,236,0.9)', boxShadow: '0 0 30px #f7ecda, 0 0 0 3px rgba(255,239,210,0.45)' }} />
                    </div>
                  </div>
                </div>

                {/* Orbit ring 3 */}
                <div className="pointer-events-none absolute box-border origin-center rounded-full" style={{ inset: '27%', border: '1px solid rgba(212,175,55,0.12)' }}>
                  <div className="relative h-full w-full origin-center" style={{ animation: 'cantian-orbit-spin 22s linear infinite' }}>
                    <div className="absolute inset-0 origin-center" style={{ transform: 'rotate(156deg)' }}>
                      <span className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ width: 10, height: 10, backgroundColor: '#ebc889', border: '1.5px solid rgba(255,248,236,0.9)', boxShadow: '0 0 31px #f2e0bf, 0 0 0 3px rgba(255,239,210,0.45)' }} />
                    </div>
                  </div>
                </div>

                {/* Orbit ring 4 */}
                <div className="pointer-events-none absolute box-border origin-center rounded-full" style={{ inset: '34%', border: '1px solid rgba(212,175,55,0.10)' }}>
                  <div className="relative h-full w-full origin-center" style={{ animation: 'cantian-orbit-spin 40s linear infinite' }}>
                    <div className="absolute inset-0 origin-center" style={{ transform: 'rotate(232deg)' }}>
                      <span className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ width: 8, height: 8, backgroundColor: '#ddb781', border: '1.5px solid rgba(255,248,236,0.9)', boxShadow: '0 0 27px #ebd7b5, 0 0 0 3px rgba(255,239,210,0.45)' }} />
                    </div>
                  </div>
                </div>

                {/* Orbit ring 5 - innermost */}
                <div className="pointer-events-none absolute box-border origin-center rounded-full" style={{ inset: '41%', border: '1px solid rgba(212,175,55,0.08)' }}>
                  <div className="relative h-full w-full origin-center" style={{ animation: 'cantian-orbit-spin 32s linear infinite' }}>
                    <div className="absolute inset-0 origin-center" style={{ transform: 'rotate(306deg)' }}>
                      <span className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ width: 8, height: 8, backgroundColor: '#d8b994', border: '1.5px solid rgba(255,248,236,0.9)', boxShadow: '0 0 26px #e8d8c2, 0 0 0 3px rgba(255,239,210,0.45)' }} />
                    </div>
                  </div>
                </div>

                {/* Labels around the chart */}
                <div className="pointer-events-none absolute inset-0">
                  {/* BAZI - left */}
                  <div className="absolute inline-flex flex-col py-1.5 left-[-2%] top-[21%] pl-2 text-left sm:left-[1%] sm:top-[18%]">
                    <span className="absolute top-1/2 h-6 w-[1.5px] -translate-y-1/2 bg-amber-600/60 left-0 sm:h-7" />
                    <p className="whitespace-nowrap uppercase text-amber-500/70 text-[7px] tracking-[0.05em] sm:text-[9px] sm:tracking-[0.1em]">BAZI</p>
                    <p className="mt-0.5 whitespace-nowrap font-semibold leading-snug text-white/70 text-[8px] sm:text-[10px]">Destiny Reading</p>
                  </div>
                  {/* QIMEN - right */}
                  <div className="absolute inline-flex flex-col py-1.5 right-[-2%] top-[31%] pr-2 text-right sm:right-[1%] sm:top-[14%]">
                    <span className="absolute top-1/2 h-6 w-[1.5px] -translate-y-1/2 bg-amber-600/60 right-0 sm:h-7" />
                    <p className="whitespace-nowrap uppercase text-amber-500/70 text-[7px] tracking-[0.05em] sm:text-[9px] sm:tracking-[0.1em]">QIMEN</p>
                    <p className="mt-0.5 whitespace-nowrap font-semibold leading-snug text-white/70 text-[8px] sm:text-[10px]">Strategy & Timing</p>
                  </div>
                  {/* TONG SHU - top center */}
                  <div className="absolute inline-flex flex-col py-1 left-1/2 top-[1%] -translate-x-1/2 pl-2 text-left">
                    <span className="absolute top-1/2 h-6 w-[1.5px] -translate-y-1/2 bg-amber-600/60 left-0 sm:h-7" />
                    <p className="whitespace-nowrap uppercase text-amber-500/70 text-[7px] tracking-[0.05em] sm:text-[9px] sm:tracking-[0.1em]">TONG SHU</p>
                    <p className="mt-0.5 whitespace-nowrap font-semibold leading-snug text-white/70 text-[8px] sm:text-[10px]">Date Selection</p>
                  </div>
                  {/* FENG SHUI - bottom left */}
                  <div className="absolute inline-flex flex-col py-1.5 left-[1%] bottom-[18%] pl-2 text-left sm:left-[4%] sm:bottom-[22%]">
                    <span className="absolute top-1/2 h-6 w-[1.5px] -translate-y-1/2 bg-amber-600/60 left-0 sm:h-7" />
                    <p className="whitespace-nowrap uppercase text-amber-500/70 text-[7px] tracking-[0.05em] sm:text-[9px] sm:tracking-[0.1em]">FENG SHUI</p>
                    <p className="mt-0.5 whitespace-nowrap font-semibold leading-snug text-white/70 text-[8px] sm:text-[10px]">Space Harmony</p>
                  </div>
                  {/* LIFECODE - bottom right */}
                  <div className="absolute inline-flex flex-col py-1.5 right-[4%] bottom-[16%] pr-2 text-right sm:right-[5%] sm:bottom-[25%]">
                    <span className="absolute top-1/2 h-6 w-[1.5px] -translate-y-1/2 bg-amber-600/60 right-0 sm:h-7" />
                    <p className="whitespace-nowrap uppercase text-amber-500/70 text-[7px] tracking-[0.05em] sm:text-[9px] sm:tracking-[0.1em]">LIFECODE</p>
                    <p className="mt-0.5 whitespace-nowrap font-semibold leading-snug text-white/70 text-[8px] sm:text-[10px]">Numerology</p>
                  </div>
                </div>
              </div>

              {/* Caption */}
              <p className="absolute -bottom-2 text-center text-[10px] text-gray-600 w-full">
                Reference: Equatorial Star Chart (Qing Dynasty, 1851)
              </p>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Bazi Bridge Section */}
      <section className="py-24 bg-[#0f0f0f] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_coursehub-158/artifacts/mdo3lb2c_Bazi_Bridge_View_from_South_2012-07.JPG"
                  alt="Bazi Bridge - Traditional Chinese Architecture"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl" />
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl" />
            </div>

            {/* Content */}
            <div className="lg:pl-8">
              <p className="text-amber-400 text-sm uppercase tracking-widest mb-4">The Origin of Wisdom</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6 leading-tight">
                The <span className="font-serif italic text-amber-200">Bazi Bridge</span>
              </h2>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  Standing for over 800 years, the Bazi Bridge (八字桥) in Shaoxing, China, is one of the oldest and most renowned ancient bridges in the country. Its unique figure-eight design represents the harmony between heaven and earth – the same principles that form the foundation of BaZi destiny analysis.
                </p>
                <p>
                  Just as the bridge connects two shores, BaZi (Four Pillars of Destiny) bridges the gap between your potential and your reality. It reveals the hidden patterns in your birth chart that influence your wealth, relationships, career, and life path.
                </p>
                <p>
                  Our courses are built on these timeless principles – practical wisdom that has guided countless individuals to make better decisions, understand their true nature, and align their actions with favorable timing.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="https://bazi.live"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-amber-500 hover:bg-amber-400 text-[#0a0a0a] font-medium px-4 py-2 rounded-md transition-colors"
                >
                  Plot Your Destiny Chart Now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
                <Button
                  variant="outline"
                  onClick={() => navigate('/consultation-fees')}
                  className="border-[#353535] text-white hover:bg-[#1a1a1a]"
                >
                  Book a Reading
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transformation Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6">
              Experience{' '}
              <span className="font-serif italic text-amber-200">guided, actionable</span>
              <br />
              Transformation
            </h2>
          </div>

          {/* Upcoming Events/Courses Card */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
            {/* Featured Event */}
            {featuredEvent && (
            <div className="lg:col-span-1">
              <Card className="bg-[#141414] border-[#252525] overflow-hidden h-full">
                <CardContent className="p-0">
                  <div className="p-4 border-b border-[#252525]">
                    <div className="flex items-center gap-2 text-amber-400 text-sm mb-1">
                      <Sparkles className="w-4 h-4" />
                      <span>Coming Up Next</span>
                    </div>
                    {featuredEvent.badge && (
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                      {featuredEvent.badge}
                    </Badge>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      {featuredEvent.title}
                    </h3>
                    <div className="space-y-2 text-sm text-gray-400 mb-6">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{featuredEvent.date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{featuredEvent.location}</span>
                      </div>
                    </div>
                    {featuredEvent.description && (
                    <p className="text-gray-400 text-sm mb-6">
                      {featuredEvent.description}
                    </p>
                    )}
                    <Button 
                      className="w-full bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20"
                      onClick={() => {
                        const link = featuredEvent.link || '/courses';
                        if (link.startsWith('http')) window.open(link, '_blank');
                        else navigate(link);
                      }}
                    >
                      View Event
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
            )}

            {/* Other Events List */}
            <div className={featuredEvent ? "lg:col-span-2" : "lg:col-span-3"}>
              <Card className="bg-[#141414] border-[#252525] h-full">
                <CardContent className="p-4 border-b border-[#252525]">
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <Calendar className="w-4 h-4" />
                    <span>All Upcoming Events</span>
                  </div>
                </CardContent>
                <CardContent className="p-0">
                  {upcomingEvents.length > 0 ? upcomingEvents.map((event, idx) => (
                    <div 
                      key={event.id || idx}
                      className="p-6 border-b border-[#252525] last:border-0 hover:bg-[#1a1a1a] transition-colors cursor-pointer group"
                      onClick={() => {
                        const link = event.link || '/courses';
                        if (link.startsWith('http')) window.open(link, '_blank');
                        else navigate(link);
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-white font-medium mb-2 group-hover:text-amber-200 transition-colors">
                            {event.title}
                          </h4>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {event.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-amber-400 transition-colors" />
                      </div>
                    </div>
                  )) : (
                    <div className="p-6 text-center text-gray-500">No upcoming events at this time.</div>
                  )}
                </CardContent>
                <CardContent className="p-4 bg-[#0f0f0f]">
                  <Button 
                    variant="ghost" 
                    className="w-full text-amber-400 hover:text-amber-300 hover:bg-transparent"
                    onClick={() => navigate('/courses')}
                  >
                    Explore All Events
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Knowledge Framework Section */}
      <section className="py-24 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
            <div className="max-w-3xl">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6 leading-tight">
                This isn't just{' '}
                <span className="font-serif italic text-amber-200">Knowledge.</span>
                <br />
                It's a practical framework
                <br />
                for{' '}
                <span className="font-serif italic text-amber-200">Real Change.</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed">
                Over 60,000 students have used this system to gain clarity, make smarter moves, and create results that last. From business leaders to everyday individuals, it works – because it's built on structure, not guesswork.
              </p>
            </div>
            <Button
              onClick={() => navigate('/courses')}
              className="mt-6 md:mt-0 bg-amber-500 hover:bg-amber-400 text-[#0a0a0a] font-medium shrink-0"
              data-testid="view-all-courses-btn"
            >
              View All Courses
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Academy Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {academies.map((academy, idx) => (
              <Card 
                key={idx}
                className="bg-[#141414] border-[#252525] overflow-hidden group cursor-pointer hover:border-[#353535] transition-all"
                onClick={() => navigate('/courses')}
              >
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={academy.image}
                    alt={academy.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-transparent to-transparent" />
                </div>
                <CardContent className="p-5 -mt-10 relative">
                  <p className="text-amber-400 text-xs uppercase tracking-wider mb-1">{academy.subtitle}</p>
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-amber-200 transition-colors">
                    {academy.title}
                  </h3>
                  <p className="text-gray-400 text-xs mb-3 line-clamp-2">
                    {academy.description}
                  </p>
                  <div className="flex items-center text-amber-400 text-sm group-hover:text-amber-300">
                    <span>Explore</span>
                    <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Qi Men Dun Jia Section */}
      <section className="py-24 bg-[#0a0a0a] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content - Left Side */}
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6 leading-tight">
                <span className="font-serif italic text-amber-200">Qi Men Dun Jia</span>
                <br />
                <span className="text-2xl md:text-3xl text-gray-400">奇門遁甲</span>
              </h2>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  Qi Men Dun Jia is an ancient system that was once restricted for use only by the Emperor and his imperial astrology advisers. Its power was so revered that knowledge of this art was kept secret within the palace walls for centuries.
                </p>
                <p>
                  The key strength of Qi Men Dun Jia lies in its ability to <span className="text-white">analyse the energy of current space and time</span>. By positioning yourself at the most auspicious energy, at the right time and direction, you can ultimately achieve the best results for your goals – whether in business, relationships, or life decisions.
                </p>
                <p>
                  Throughout Chinese history, Qi Men Dun Jia has been recorded alongside <span className="text-amber-200">Da Liu Ren (大六壬)</span> and <span className="text-amber-200">Tai Yi Shen Shu (太乙神數)</span> as the highest <span className="text-white">Three Arts or Three Styles (三式 sān shì)</span> in Chinese Metaphysics – the pinnacle of strategic divination.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="https://qmdj.live"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-amber-500 hover:bg-amber-400 text-[#0a0a0a] font-medium px-4 py-2 rounded-md transition-colors"
                >
                  Access Qimen Plotter
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
                <Button
                  variant="outline"
                  onClick={() => navigate('/consultation-fees')}
                  className="border-[#353535] text-white hover:bg-[#1a1a1a]"
                >
                  Book a Forecasting Session
                </Button>
              </div>
            </div>

            {/* Image - Right Side */}
            <div className="relative order-1 lg:order-2">
              <div className="aspect-square rounded-2xl overflow-hidden border border-[#252525]">
                <img
                  src="https://customer-assets.emergentagent.com/job_coursehub-158/artifacts/zsp2jvhg_ChatGPT%20Image%20Feb%207%2C%202026%2C%2001_51_41%20PM.png"
                  alt="Qi Men Dun Jia - Ancient Chinese Divination Chart"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl" />
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Signature of Wealth Section */}
      <section className="py-24 bg-[#0f0f0f] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image - Left Side */}
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden border border-[#252525]">
                <img
                  src="https://customer-assets.emergentagent.com/job_6499a36b-d5eb-47ae-af32-473f14f7d9c5/artifacts/epdhb3jl_Gemini_Generated_Image_yggvdvyggvdvyggv.png"
                  alt="Signature of Wealth - Chinese Calligraphy"
                  className="w-full h-full object-cover scale-[1.15] translate-y-[5%] hover:scale-[1.25] transition-transform duration-700"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl" />
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl" />
            </div>

            {/* Content - Right Side */}
            <div className="lg:pl-8">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-8 leading-tight">
                <span className="font-serif italic text-amber-200">Signature</span> of Wealth
              </h2>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-400 mt-1 shrink-0" />
                  <p className="text-gray-300">
                    Discover the <span className="text-white">3 core strokes</span> that create a powerful, wealth-attracting signature — and why most people get it wrong
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-400 mt-1 shrink-0" />
                  <p className="text-gray-300">
                    Learn how your current signature could be <span className="text-white">silently blocking money, confidence, and growth</span> (and what to change now)
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-400 mt-1 shrink-0" />
                  <p className="text-gray-300">
                    Understand the connection between <span className="text-white">Feng Shui, Qi energy</span>, and how your signature affects your destiny
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-400 mt-1 shrink-0" />
                  <p className="text-gray-300">
                    Learn real-world examples of what <span className="text-white">successful signatures</span> look like, and how to design your own for maximum influence
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-400 mt-1 shrink-0" />
                  <p className="text-gray-300">
                    Watch <span className="text-white">live signature analysis</span> in action — and uncover how small changes create massive shifts in personal energy
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <a
                  href={siteLinks.signatureOfWealth?.url || 'https://fengshuisignature.com/optin'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-amber-500 hover:bg-amber-400 text-[#0a0a0a] font-medium px-4 py-2 rounded-md transition-colors"
                >
                  {siteLinks.signatureOfWealth?.text || 'Join the upcoming webinar'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chinese Face Reading Section */}
      <section className="py-24 bg-[#0a0a0a] overflow-hidden" data-testid="face-reading-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content - Left Side */}
            <div>
              <p className="text-amber-400 text-sm font-medium tracking-widest uppercase mb-4">Ancient Wisdom</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-4 leading-tight">
                <span className="font-serif italic text-amber-200">Chinese Face Reading</span>
              </h2>
              <p className="text-gray-400 text-lg mb-3">(Mian Xiang, 面相)</p>

              <p className="text-gray-300 leading-relaxed mb-6">
                Chinese face reading is an ancient practice within Chinese metaphysics that dates back more than 2,000 years. It is based on the belief that a person's facial features — such as the shape of the forehead, eyes, nose, mouth, and jaw — can reflect aspects of their <span className="text-white font-medium">personality, health, life tendencies, and fortune cycles</span>. Practitioners believe the face acts like a "map" of a person's life, with different areas corresponding to different ages and aspects of life such as wealth, relationships, and career.
              </p>

              <p className="text-gray-300 leading-relaxed mb-8">
                Through the concept of the <span className="text-white font-medium">12 Palaces (十二宫)</span>, different areas of the face correspond to specific aspects of a person's life, each known as a "palace." These palaces are believed to reveal tendencies related to personality, fortune, relationships, and life opportunities.
              </p>

              <div className="inline-flex items-center bg-gray-600/50 text-gray-400 font-medium px-6 py-3 rounded-md cursor-not-allowed opacity-60">
                Coming Soon
              </div>
            </div>

            {/* Image - Right Side */}
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden border border-[#252525]">
                <img
                  src={getSec('face_reading', 'image', 'https://customer-assets.emergentagent.com/job_6499a36b-d5eb-47ae-af32-473f14f7d9c5/artifacts/w1nyi1iz_ChatGPT%20Image%20Mar%2014%2C%202026%2C%2009_55_34%20AM.png')}
                  alt="Chinese Face Reading - Mian Xiang 面相 - The 12 Palaces"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl" />
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Tong Shu - Energy of Time Section */}
      <section className="py-24 bg-[#0f0f0f] overflow-hidden" data-testid="tongshu-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image - Left Side */}
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden border border-[#252525]">
                <img
                  src={getSec('tongshu', 'image', 'https://customer-assets.emergentagent.com/job_6499a36b-d5eb-47ae-af32-473f14f7d9c5/artifacts/g2nvjrai_ChatGPT%20Image%20Mar%2015%2C%202026%2C%2012_07_47%20AM.png')}
                  alt="Online Tong Shu - Know the Energy of the Day"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl" />
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl" />
            </div>

            {/* Content - Right Side */}
            <div className="lg:pl-8">
              <p className="text-amber-400 text-sm font-medium tracking-widest uppercase mb-4">Chinese Almanac</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-8 leading-tight">
                Understand the <span className="font-serif italic text-amber-200">Energy of Time</span>
              </h2>

              <p className="text-gray-300 leading-relaxed mb-6">
                Every day carries a different energy. Some days support progress and success, while others are better for <span className="text-white font-medium">rest, preparation, or avoiding major decisions</span>.
              </p>

              <p className="text-gray-300 leading-relaxed mb-6">
                <span className="text-white font-medium">OnlineTongShu.com</span> brings the traditional Chinese Tong Shu (Almanac) into a modern, easy-to-use platform.
              </p>

              <p className="text-gray-300 leading-relaxed mb-6">
                Our system analyzes the daily energies and presents them in a clear visual format, including:
              </p>

              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-gray-300">Daily auspicious and inauspicious activities</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-gray-300">Monthly energy overview charts</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-gray-300">Color-coded calendars for quick decision making</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-gray-300">Identification of the best periods to act</p>
                </div>
              </div>

              <p className="text-gray-400 leading-relaxed mb-8">
                Instead of guessing when to move forward, you can plan strategically with time itself on your side.
              </p>

              {renderCta('tongshu', "Check Today's Energy", 'https://onlinetongshu.com', true)}
            </div>
          </div>
        </div>
      </section>

      {/* Feng Shui Plotter Section */}
      <section className="py-24 bg-[#0a0a0a] overflow-hidden" data-testid="fengshui-plotter-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content - Left Side */}
            <div>
              <p className="text-amber-400 text-sm font-medium tracking-widest uppercase mb-4">Digital Feng Shui Tool</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-8 leading-tight">
                <span className="font-serif italic text-amber-200">Feng Shui Plotter</span>
              </h2>

              <p className="text-gray-300 leading-relaxed mb-6">
                Feng Shui Plotter transforms your floor plan into a <span className="text-white font-medium">powerful Feng Shui map in seconds</span>.
              </p>

              <p className="text-gray-300 leading-relaxed mb-6">
                Simply upload your floor plan and instantly discover your home's center point, align the <span className="text-white font-medium">24 Mountains</span>, and reveal the true directional energy of your space using a professional Luo Pan system.
              </p>

              <p className="text-gray-300 leading-relaxed mb-6">
                You can also easily identify your <span className="text-white font-medium">best life directions</span> (secret feature) — helping you choose the most supportive directions for work, success, health, nobleman and opportunities.
              </p>

              <p className="text-gray-300 leading-relaxed mb-2">No complicated calculations.</p>
              <p className="text-gray-300 leading-relaxed mb-6">No guessing.</p>

              <p className="text-white font-medium leading-relaxed mb-8">
                Just clear Feng Shui insights to help you align your home and your life.
              </p>

              {renderCta('fengshui', 'Start Plotting Now', 'https://fengshuiplotter.live', true)}
            </div>

            {/* Image - Right Side */}
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden border border-[#252525]">
                <img
                  src={getSec('fengshui', 'image', 'https://customer-assets.emergentagent.com/job_461f73ce-00d7-4bc6-9fca-0baf1a22099b/artifacts/crotgjos_Screen%20Shot%202026-03-18%20at%204.46.01%20pm.png')}
                  alt="Feng Shui Plotter - Plot Your Feng Shui Instantly"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl" />
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Life Code (Numerology) Section */}
      <section className="py-24 bg-[#0f0f0f] overflow-hidden" data-testid="numerology-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image - Left Side */}
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden border border-[#252525]">
                <img
                  src={getSec('numerology', 'image', 'https://customer-assets.emergentagent.com/job_6499a36b-d5eb-47ae-af32-473f14f7d9c5/artifacts/aky7rk1b_ChatGPT%20Image%20Mar%2014%2C%202026%2C%2001_44_27%20PM.png')}
                  alt="LifeCode - Unlock Your Destiny through Numerology"
                  className="w-full h-full object-cover object-[50%_35%] hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl" />
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl" />
            </div>

            {/* Content - Right Side */}
            <div className="lg:pl-8">
              <p className="text-amber-400 text-sm font-medium tracking-widest uppercase mb-4">Decode Your Numbers</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-8 leading-tight">
                <span className="font-serif italic text-amber-200">Life Code</span> <span className="text-gray-400 text-2xl md:text-3xl">(Numerology)</span>
              </h2>

              <p className="text-gray-300 leading-relaxed mb-6">
                Numerology is a divination system that analyzes the mystical relationship between numbers, names, and birth dates to reveal a person's <span className="text-white font-medium">character, strengths, challenges, and life purpose</span>.
              </p>

              <p className="text-gray-300 leading-relaxed mb-6">
                The belief behind numerology is that numbers carry unique vibrational energies. These energies influence a person's personality, behavior, opportunities, and the path they are likely to follow in life.
              </p>

              <p className="text-gray-300 leading-relaxed mb-6">
                By converting letters and birth dates into numbers, numerology interprets patterns that provide insight into a person's <span className="text-white font-medium">inner nature and life direction</span>.
              </p>

              <p className="text-gray-300 leading-relaxed mb-8">
                The most important numbers in numerology are known as <span className="text-white font-medium">core numbers</span>, which form the foundation of a person's numerological profile.
              </p>

              <div className="inline-flex items-center bg-gray-600/50 text-gray-400 font-medium px-6 py-3 rounded-md cursor-not-allowed opacity-60">
                Coming Soon
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consultation Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6 leading-tight">
              You don't need more motivation.
              <br />
              You need a{' '}
              <span className="font-serif italic text-amber-200">Map.</span>
              <br />
              Let's Draw One That Fits.
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed">
              Planning a major move in career, business, or life can feel clear at first, but often belies uncertainty beneath the surface. That's where our consultants step in. We help you cut through the noise, align your timing, and create a clear path forward.
            </p>
          </div>

          {/* Consultation Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {consultationTypes.map((item, idx) => (
              <Card 
                key={idx}
                className="bg-[#141414] border-[#252525] hover:border-[#353535] transition-all group"
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center shrink-0">
                      <item.icon className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-amber-400 text-sm mb-1">{item.category}</p>
                      <h3 className="text-xl font-semibold text-white">{item.title}</h3>
                    </div>
                  </div>
                  <p className="text-gray-400 mb-6">{item.description}</p>
                  <Button 
                    variant="outline"
                    className="border-[#353535] text-white hover:bg-[#252525] hover:text-amber-200"
                    onClick={() => navigate('/courses')}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats & Trust Section */}
      <section className="py-24 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-2">
                  {stat.value}
                </p>
                <p className="text-gray-500 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steven Yong Profile Section */}
      <section className="relative py-32 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_coursehub-158/artifacts/mbp7cssq_AHKP4615.JPG"
            alt="Steven Yong"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/95 via-[#0a0a0a]/70 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-amber-400 text-sm uppercase tracking-[0.2em] mb-4" data-testid="landing-profile-label">Profile</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-4 leading-tight">
              <span className="font-serif italic text-amber-200">Steven Yong</span>
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-lg leading-relaxed">
              Translating Ancient Strategy into Modern Advantage
            </p>
            <Button
              onClick={() => { navigate('/about'); window.scrollTo(0, 0); }}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base"
              data-testid="landing-profile-btn"
            >
              Read His Story
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6">
              <span className="font-serif italic text-amber-200">Authentic</span> Insights.
              <br />
              <span className="font-serif italic text-amber-200">Measurable</span> Impact.
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              They didn't just learn something new. They changed how they approach decisions, timing, and opportunities – and their results.
            </p>
          </div>

          {/* Testimonial Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mockTestimonials.slice(0, 3).map((testimonial, idx) => (
              <Card 
                key={testimonial.id}
                className="bg-[#141414] border-[#252525] hover:border-[#353535] transition-all"
              >
                <CardContent className="p-8">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <Avatar className="w-12 h-12 border-2 border-[#252525]">
                      <AvatarImage src={testimonial.avatar} />
                      <AvatarFallback className="bg-amber-500/10 text-amber-400">
                        {testimonial.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-white font-medium">{testimonial.name}</p>
                      <p className="text-gray-500 text-sm">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6 leading-tight">
            Not Sure{' '}
            <span className="font-serif italic text-amber-200">Where To Start?</span>
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
            We're here to help. Reach out and we'll guide you to your best next step, whether it's a reading, course, or personalized consultation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-amber-500 hover:bg-amber-400 text-[#0a0a0a] font-medium px-8 py-6 text-base"
              data-testid="cta-explore-btn"
            >
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/courses')}
              className="border-[#353535] text-white hover:bg-[#1a1a1a] px-8 py-6 text-base"
            >
              Explore Courses
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
